import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  FileText,
  Loader2,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  creerModeleMessage,
  fetchModelesMessage,
  modifierModeleMessage,
  restaurerModelesMessage,
  supprimerModeleMessage,
  type ModeleMessage,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

const VIDE: Partial<ModeleMessage> = {
  famille: 'Divers',
  titre: '',
  audience: '',
  sujet: '',
  corps: '',
};

/**
 * Les modèles de message, tels que l'association les tient.
 *
 * Ils étaient écrits dans le code : les mêmes pour tout le monde, et
 * intouchables. Or une association qui s'en sert les adapte dès le deuxième
 * envoi — sa formule de politesse, le nom de sa salle des fêtes, le numéro
 * qu'on appelle en cas d'empêchement. Ne pas pouvoir enregistrer cette
 * adaptation revient à la refaire à chaque fois, donc à cesser de s'en servir.
 *
 * Ce qui reste entre crochets y reste : les modèles fournis sont des départs,
 * et remplacer les crochets par des valeurs plausibles est le meilleur moyen
 * qu'un message parte un jour avec la date de l'exemple.
 */
export default function ModelesMessagePage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peut = aLeDroit('diffuser_messages');

  const [edite, setEdite] = useState<Partial<ModeleMessage> | null>(null);
  const [aSupprimer, setASupprimer] = useState<ModeleMessage | null>(null);
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['modeles-message'],
    queryFn: fetchModelesMessage,
    enabled: peut,
  });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['modeles-message'] });
  const echec = (e: Error) => setNotice({ type: 'erreur', texte: e.message });

  const enregistrer = useMutation({
    mutationFn: () =>
      edite?.id ? modifierModeleMessage(edite.id, edite) : creerModeleMessage(edite ?? {}),
    onSuccess: () => {
      setNotice({ type: 'ok', texte: 'Modèle enregistré.' });
      setEdite(null);
      rafraichir();
      // La liste de l'écran de diffusion lit la même table.
      queryClient.invalidateQueries({ queryKey: ['diffusions'] });
    },
    onError: echec,
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => supprimerModeleMessage(id),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      setASupprimer(null);
      rafraichir();
      queryClient.invalidateQueries({ queryKey: ['diffusions'] });
    },
    onError: echec,
  });

  const restaurer = useMutation({
    mutationFn: restaurerModelesMessage,
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      rafraichir();
      queryClient.invalidateQueries({ queryKey: ['diffusions'] });
    },
    onError: echec,
  });

  if (authLoading || (peut && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peut) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Régler les modèles dépend du même droit qu'écrire à un groupe.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const modeles = data?.modeles ?? [];
  const familles = [...new Set(modeles.map((m) => m.famille))];

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-center gap-3">
        <FileText className="w-7 h-7 text-primary-700 shrink-0" />
        <div className="flex-1 min-w-[14rem]">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Modèles de message
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Vos départs de message, adaptés à votre association et à vos habitudes.
          </p>
        </div>
        <Link to="/bureau/diffusion" className="btn-outline text-sm inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Retour à la diffusion
        </Link>
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">
            Fermer
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary text-sm inline-flex items-center gap-2"
          onClick={() => setEdite({ ...VIDE })}
        >
          <Plus className="w-4 h-4" /> Nouveau modèle
        </button>

        {(data?.fournisManquants ?? 0) > 0 && (
          <button
            type="button"
            className="btn-outline text-sm inline-flex items-center gap-2"
            disabled={restaurer.isPending}
            onClick={() => restaurer.mutate()}
          >
            {restaurer.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Réinstaller les {data!.fournisManquants} modèle
            {data!.fournisManquants > 1 ? 's' : ''} fourni
            {data!.fournisManquants > 1 ? 's' : ''} manquant
            {data!.fournisManquants > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {(data?.fournisManquants ?? 0) > 0 && (
        <p className="text-xs text-gray-600 dark:text-gray-400">
          La réinstallation ne touche pas ce que vous avez écrit : un modèle fourni que vous avez
          corrigé porte vos mots, et les écraser sous couvert de vous rendre service serait effacer
          votre travail.
        </p>
      )}

      {modeles.length === 0 && (
        <p className="card text-sm text-gray-600 dark:text-gray-400">
          Aucun modèle. Créez le vôtre, ou réinstallez ceux fournis avec le logiciel.
        </p>
      )}

      {familles.map((famille) => (
        <section key={famille} className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-1">
            {famille}
          </h2>
          <div className="space-y-2">
            {modeles
              .filter((m) => m.famille === famille)
              .map((m) => (
                <div key={m.id} className="card flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100 flex flex-wrap items-center gap-2">
                      {m.titre}
                      {m.fourni && (
                        <span className="text-[0.65rem] uppercase tracking-wide rounded-full border border-gray-300 dark:border-gray-600 px-2 py-0.5 text-gray-500 dark:text-gray-400">
                          fourni
                        </span>
                      )}
                      {m.audience && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          → {data?.audiences.find((a) => a.code === m.audience)?.libelle ?? m.audience}
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{m.sujet}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2 whitespace-pre-line">
                      {m.corps.slice(0, 180)}
                      {m.corps.length > 180 ? '…' : ''}
                    </p>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      className="btn-outline text-sm"
                      onClick={() => setEdite({ ...m })}
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      title="Supprimer ce modèle"
                      className="text-gray-400 hover:text-red-600 p-2"
                      onClick={() => setASupprimer(m)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </section>
      ))}

      {/* L'édition, en fenêtre : le corps d'un message demande de la place, et
          l'écrire dans une ligne du tableau reviendrait à ne pas le relire. */}
      {edite !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
            <div className="p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {edite.id ? 'Modifier le modèle' : 'Nouveau modèle'}
              </h3>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-gray-700 dark:text-gray-300">Titre</span>
                  <input
                    className="input mt-1"
                    value={edite.titre ?? ''}
                    placeholder="Ce qu'on lira dans la liste"
                    onChange={(e) => setEdite({ ...edite, titre: e.target.value })}
                  />
                </label>

                <label className="block text-sm">
                  <span className="text-gray-700 dark:text-gray-300">Famille</span>
                  <input
                    className="input mt-1"
                    list="familles-modeles"
                    value={edite.famille ?? ''}
                    placeholder="Bénévoles, Adhérents, Sponsors…"
                    onChange={(e) => setEdite({ ...edite, famille: e.target.value })}
                  />
                  <datalist id="familles-modeles">
                    {familles.map((f) => (
                      <option key={f} value={f} />
                    ))}
                  </datalist>
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300">Liste proposée</span>
                <select
                  className="input mt-1"
                  value={edite.audience ?? ''}
                  onChange={(e) => setEdite({ ...edite, audience: e.target.value })}
                >
                  <option value="">— aucune, à choisir au moment d'écrire —</option>
                  {(data?.audiences ?? []).map((a) => (
                    <option key={a.code} value={a.code}>
                      {a.libelle}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                  Proposée, jamais imposée : elle reste modifiable au moment d'envoyer, et
                  d'autres listes peuvent s'y ajouter.
                </span>
              </label>

              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300">Sujet</span>
                <input
                  className="input mt-1"
                  value={edite.sujet ?? ''}
                  placeholder="Convocation à l'assemblée générale du [date]"
                  onChange={(e) => setEdite({ ...edite, sujet: e.target.value })}
                />
              </label>

              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300">Message</span>
                <textarea
                  className="input mt-1 font-mono text-xs"
                  rows={14}
                  value={edite.corps ?? ''}
                  placeholder={'Bonjour,\n\n…\n\n[votre nom], [votre fonction]'}
                  onChange={(e) => setEdite({ ...edite, corps: e.target.value })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                  Laissez entre crochets ce qui change à chaque envoi. Un modèle rempli de valeurs
                  plausibles finit par partir tel quel, avec la date de l'exemple.
                </span>
              </label>

              <div className="flex flex-wrap gap-2 justify-end pt-1">
                <button type="button" className="btn-outline text-sm" onClick={() => setEdite(null)}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn-primary text-sm inline-flex items-center gap-2"
                  disabled={
                    enregistrer.isPending ||
                    !(edite.titre ?? '').trim() ||
                    !(edite.sujet ?? '').trim() ||
                    !(edite.corps ?? '').trim()
                  }
                  onClick={() => enregistrer.mutate()}
                >
                  {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {aSupprimer !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl">
            <div className="p-5 space-y-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Supprimer « {aSupprimer.titre} » ?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Le modèle disparaît de la liste au moment d'écrire. Les messages déjà envoyés ne
                sont pas touchés.
                {aSupprimer.fourni &&
                  " Ce modèle est fourni avec le logiciel : vous pourrez le réinstaller plus tard."}
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  className="btn-outline text-sm"
                  onClick={() => setASupprimer(null)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-60"
                  disabled={supprimer.isPending}
                  onClick={() => supprimer.mutate(aSupprimer.id)}
                >
                  {supprimer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
